import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import _ from "lodash";
import { NOOP } from "../constants/labeler";
import { LabeledCharacter, UniquePart } from "./models/uniqueParts";
import { Parameter } from "./models/parameters";

export interface ClassifierUniquePartSummary {
  id: string;
  value_text: string;
  labeled: boolean;
}

export interface ClassifierUniqueParts {
  parts: ClassifierUniquePartSummary[];
  selectedPart: UniquePart | null;
  selectedCharacterIndex: number;
  stagedParameters: { [id: number]: string };
  labeledCharacters: { [id: number]: string };
  dirty: boolean;
}

export const NOOP_PARAMETER: Parameter = {
  id: NOOP,
  name: NOOP,
  short_name: NOOP,
  used_for_component_matching: true,
  unit: null,
  short_unit: null,
};

export const transformBackendLabelsToFrontendLabels = (
  labeledValueText: LabeledCharacter[],
): { [id: number]: string } => {
  return _.reduce(
    labeledValueText,
    (
      result: { [id: number]: string },
      labeledCharacter: LabeledCharacter,
      index: number,
    ) => {
      if (labeledCharacter.noop) {
        result[index] = NOOP;
      } else {
        result[index] = labeledCharacter.parameter as string;
      }
      return result;
    },
    {} as { [id: number]: string },
  );
};

export const transformFrontendLabelsToBackendLabels = (
  part: UniquePart,
  labeledCharacters: { [id: number]: string },
): LabeledCharacter[] => {
  return [...part.value_text].map((character, characterIndex) => {
    const parameterId = labeledCharacters[characterIndex];
    if (parameterId == NOOP) {
      return { character, noop: true };
    } else {
      return {
        character,
        noop: false,
        parameter: parameterId,
      };
    }
  });
};

export const partsSlice = createSlice({
  name: "parts",
  initialState: {
    loading: false,
    errored: false,
    parts: [],
    selectedPart: null,
    selectedCharacterIndex: 0,
    stagedParameters: { 0: NOOP },
    labeledCharacters: {},
    dirty: false,
  } as ClassifierUniqueParts,
  reducers: {
    partsLoaded: (
      state,
      action: PayloadAction<{ parts: ClassifierUniquePartSummary[] }>,
    ) => {
      state.parts = action.payload.parts;
      state.selectedCharacterIndex = 0;
    },
    selectPart: (state, action: PayloadAction<{ part: UniquePart }>) => {
      state.dirty = false;
      state.selectedPart = action.payload.part;
      state.selectedCharacterIndex = 0;
      state.labeledCharacters = transformBackendLabelsToFrontendLabels(
        action.payload.part.labeled_value_text,
      );
    },
    selectCharacter: (
      state,
      action: PayloadAction<{ selectedCharacterIndex: number }>,
    ) => {
      state.selectedCharacterIndex = action.payload.selectedCharacterIndex;
    },
    labelCharacter: (state, action: PayloadAction<{ keyNumber: number }>) => {
      state.dirty = true;
      state.labeledCharacters[state.selectedCharacterIndex] = state
        .stagedParameters[action.payload.keyNumber] as string;
      if (
        state.selectedCharacterIndex <
        (state.selectedPart?.value_text.length || 0) - 1
      ) {
        state.selectedCharacterIndex += 1;
      }
    },
    selectStagedParameter: (
      state,
      action: PayloadAction<{ keyNumber: number; parameterId: string }>,
    ) => {
      state.stagedParameters[action.payload.keyNumber] =
        action.payload.parameterId;
    },
    clearLabels: (state) => {
      state.dirty = false;
      state.labeledCharacters = {};
    },
    markSaved: (state, action: PayloadAction<{ partId: string }>) => {
      const selectedPartIndex = state.parts.findIndex((part) => {
        return part.id == action.payload.partId;
      });
      (state.parts[selectedPartIndex] as ClassifierUniquePartSummary).labeled =
        true;
      state.dirty = false;
    },
    markDirtyAfterSaveFailed: (state) => {
      state.dirty = true;
    },
  },
});

export const {
  partsLoaded,
  selectPart,
  selectCharacter,
  labelCharacter,
  selectStagedParameter,
  clearLabels,
  markSaved,
  markDirtyAfterSaveFailed,
} = partsSlice.actions;

export default partsSlice.reducer;
